import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, X, UserMinus } from "lucide-react";
import { api, GroupMember, GroupPurpose, HospitalGroup } from "../lib/api";
import { isAnalystOrHigher, useAuth } from "../lib/auth";

const PURPOSES: GroupPurpose[] = ["control_group", "peer_cohort", "intervention_target", "custom"];
const PURPOSE_LABELS: Record<GroupPurpose, string> = {
  control_group: "Контрольна",
  peer_cohort: "Peer-когорта",
  intervention_target: "Ціль інтервенції",
  custom: "Власна",
};

export function GroupsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const canEdit = isAnalystOrHigher(user?.role);

  const { data, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: () => api.get<HospitalGroup[]>("/api/v1/groups"),
  });

  const create = useMutation({
    mutationFn: (p: any) => api.post<HospitalGroup>("/api/v1/groups", p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      setAdding(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["groups"] }),
  });

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;

  if (selected) {
    return <GroupDetail groupId={selected} canEdit={canEdit} onBack={() => setSelected(null)} />;
  }

  return (
    <div style={{ padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h1 style={{ fontSize: 22, color: "#1a365d", margin: 0 }}>
          Групи лікарень {data && <span style={{ color: "#718096", fontSize: 14 }}>({data.length})</span>}
        </h1>
        {canEdit && (
          <button onClick={() => setAdding(true)} style={btnPrimary}>
            <Plus size={14} /> Нова група
          </button>
        )}
      </div>
      <p style={{ color: "#718096", fontSize: 13, marginBottom: 16 }}>
        Групи для D-i-D аналізу: контрольні (порівняння), peer-когорти, цілі інтервенцій або довільні підбірки.
      </p>

      {data && data.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#718096", background: "white", borderRadius: 6, border: "1px dashed #cbd5e0" }}>
          Поки що немає груп. {canEdit && "Натисніть «Нова група», щоб створити першу."}
        </div>
      )}

      {data && data.length > 0 && (
        <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
              <th style={th}>Назва</th>
              <th style={th}>Тип</th>
              <th style={th}>Опис</th>
              <th style={{ ...th, textAlign: "right" }}>Лікарень</th>
              <th style={th}>Створено</th>
              {canEdit && <th style={th}>Дії</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((g) => (
              <tr key={g.group_id} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={td}>
                  <button onClick={() => setSelected(g.group_id)}
                          style={{ background: "none", border: "none", color: g.color || "#2c5282", cursor: "pointer", fontWeight: 600, padding: 0, fontSize: 13 }}>
                    {g.name}
                  </button>
                </td>
                <td style={td}><span style={purposeBadge(g.purpose)}>{PURPOSE_LABELS[g.purpose]}</span></td>
                <td style={{ ...td, color: "#4a5568", maxWidth: 320 }}>{g.description || "—"}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{g.member_count}</td>
                <td style={{ ...td, color: "#718096", fontSize: 12 }}>
                  {new Date(g.created_at).toLocaleDateString("uk-UA")}
                </td>
                {canEdit && (
                  <td style={td}>
                    <button onClick={() => confirm(`Видалити групу «${g.name}»?`) && del.mutate(g.group_id)}
                            style={{ ...btnSecondary, color: "#e53e3e", borderColor: "#fed7d7" }}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <Modal onClose={() => setAdding(false)}>
          <CreateForm onSubmit={(p) => create.mutate(p)}
                      error={create.error ? (create.error as any).detail : null} />
        </Modal>
      )}
    </div>
  );
}

function GroupDetail({ groupId, canEdit, onBack }: { groupId: string; canEdit: boolean; onBack: () => void }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: group } = useQuery({
    queryKey: ["groups"],
    queryFn: () => api.get<HospitalGroup[]>("/api/v1/groups"),
    select: (gs) => gs.find((g) => g.group_id === groupId),
  });

  const { data: members, isLoading } = useQuery({
    queryKey: ["group-members", groupId],
    queryFn: () => api.get<GroupMember[]>(`/api/v1/groups/${groupId}/members`),
  });

  const addMembers = useMutation({
    mutationFn: (edrpous: string[]) =>
      api.post<{ added: number; invalid: string[] }>(`/api/v1/groups/${groupId}/members`, { edrpous }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["group-members", groupId] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      setAdding(false);
      if (res.invalid.length) alert(`Додано ${res.added}. Не знайдено: ${res.invalid.join(", ")}`);
    },
  });

  const removeMember = useMutation({
    mutationFn: (edrpou: string) => api.delete(`/api/v1/groups/${groupId}/members/${edrpou}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-members", groupId] });
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  if (isLoading || !group) return <div style={{ padding: 24 }}>Завантаження…</div>;

  const avg = members && members.length
    ? members.filter((m) => m.score_composite != null).reduce((s, m) => s + (m.score_composite || 0), 0) /
      members.filter((m) => m.score_composite != null).length
    : null;

  return (
    <div style={{ padding: "24px 32px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "#4299e1", cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, padding: 0 }}>
        <ArrowLeft size={14} /> До груп
      </button>

      <div style={{ marginTop: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, color: group.color || "#1a365d", margin: "0 0 4px" }}>{group.name}</h1>
        <div style={{ fontSize: 13, color: "#718096" }}>
          <span style={purposeBadge(group.purpose)}>{PURPOSE_LABELS[group.purpose]}</span>
          {" · "}{members?.length || 0} лікарень
          {avg !== null && <> · сер. Composite: <strong>{avg.toFixed(1)}</strong></>}
        </div>
        {group.description && <div style={{ marginTop: 8, fontSize: 13, color: "#4a5568" }}>{group.description}</div>}
      </div>

      {canEdit && (
        <button onClick={() => setAdding(true)} style={{ ...btnPrimary, marginBottom: 12 }}>
          <Plus size={14} /> Додати лікарень
        </button>
      )}

      {members && members.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#718096", background: "white", borderRadius: 6, border: "1px dashed #cbd5e0" }}>
          Група порожня. Додайте лікарень за ЄДРПОУ.
        </div>
      )}

      {members && members.length > 0 && (
        <table style={{ width: "100%", background: "white", borderCollapse: "collapse", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#ebf4ff", color: "#2c5282", textAlign: "left" }}>
              <th style={th}>Назва</th>
              <th style={th}>Область</th>
              <th style={th}>Тип</th>
              <th style={{ ...th, textAlign: "right" }}>Composite</th>
              {canEdit && <th style={th}></th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.edrpou} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={td}>
                  <Link to={`/facilities/${m.edrpou}`} style={{ color: "#2c5282", textDecoration: "none" }}>
                    {m.name.length > 60 ? m.name.slice(0, 60) + "…" : m.name}
                  </Link>
                  <div style={{ fontSize: 11, color: "#a0aec0" }}>ЄДРПОУ {m.edrpou}</div>
                </td>
                <td style={td}>{m.area || "—"}</td>
                <td style={td}>{m.care_type || "—"}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600, color: scoreColor(m.score_composite) }}>
                  {m.score_composite ?? "—"}
                </td>
                {canEdit && (
                  <td style={td}>
                    <button onClick={() => confirm(`Прибрати ${m.edrpou} з групи?`) && removeMember.mutate(m.edrpou)}
                            style={{ ...btnSecondary, color: "#e53e3e", borderColor: "#fed7d7" }}>
                      <UserMinus size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <Modal onClose={() => setAdding(false)}>
          <AddMembersForm
            onSubmit={(edrpous) => addMembers.mutate(edrpous)}
            error={addMembers.error ? (addMembers.error as any).detail : null}
          />
        </Modal>
      )}
    </div>
  );
}

function CreateForm({ onSubmit, error }: { onSubmit: (p: any) => void; error: string | null }) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState<GroupPurpose>("custom");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#3182ce");

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name, purpose, description: description || null, color }); }}>
      <h3 style={{ marginTop: 0 }}>Нова група лікарень</h3>
      <Field label="Назва"><input value={name} onChange={(e) => setName(e.target.value)} required style={inp} /></Field>
      <Field label="Тип">
        <select value={purpose} onChange={(e) => setPurpose(e.target.value as GroupPurpose)} style={inp}>
          {PURPOSES.map((p) => <option key={p} value={p}>{PURPOSE_LABELS[p]}</option>)}
        </select>
      </Field>
      <Field label="Опис (опційно)">
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inp, minHeight: 60 }} />
      </Field>
      <Field label="Колір (для UI)">
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ ...inp, height: 36, padding: 2 }} />
      </Field>
      {error && <div style={{ color: "#c53030", fontSize: 13, margin: "8px 0" }}>{error}</div>}
      <button type="submit" style={{ ...btnPrimary, marginTop: 12 }}>Створити</button>
    </form>
  );
}

function AddMembersForm({ onSubmit, error }: { onSubmit: (edrpous: string[]) => void; error: string | null }) {
  const [text, setText] = useState("");
  return (
    <form onSubmit={(e: FormEvent) => {
      e.preventDefault();
      const edrpous = text.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      if (edrpous.length) onSubmit(edrpous);
    }}>
      <h3 style={{ marginTop: 0 }}>Додати лікарень</h3>
      <Field label="ЄДРПОУ через кому/новий рядок (до 500)">
        <textarea value={text} onChange={(e) => setText(e.target.value)} required
                  style={{ ...inp, minHeight: 140, fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                  placeholder="00184945, 42519264&#10;34709124" />
      </Field>
      {error && <div style={{ color: "#c53030", fontSize: 13, margin: "8px 0" }}>{error}</div>}
      <button type="submit" style={{ ...btnPrimary, marginTop: 12 }}>Додати</button>
    </form>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} className="modal-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="modal-card" style={{ width: 480, maxWidth: "92vw", position: "relative" }}>
        <button onClick={onClose} aria-label="close"
                style={{ position: "absolute", top: 12, right: 12, width: 28, height: 28, borderRadius: "50%", background: "transparent", color: "var(--c-text-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--c-surface-hover)"; e.currentTarget.style.color = "var(--c-danger)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--c-text-muted)"; }}>
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function purposeBadge(purpose: GroupPurpose): React.CSSProperties {
  const colors: Record<GroupPurpose, string> = {
    control_group: "#3182ce", peer_cohort: "#38a169",
    intervention_target: "#dd6b20", custom: "#718096",
  };
  return {
    display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
    color: colors[purpose], background: colors[purpose] + "15",
  };
}

function scoreColor(v: number | null | undefined): string {
  if (v === null || v === undefined) return "#a0aec0";
  if (v >= 75) return "#38a169";
  if (v >= 50) return "#d69e2e";
  if (v >= 25) return "#dd6b20";
  return "#e53e3e";
}

const inp: React.CSSProperties = { padding: "7px 10px", fontSize: 13, border: "1px solid #cbd5e0", borderRadius: 4, outline: "none", width: "100%", boxSizing: "border-box" };
const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 600, fontSize: 12 };
const td: React.CSSProperties = { padding: "8px 12px" };
const btnPrimary: React.CSSProperties = { padding: "7px 14px", background: "#2c5282", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 };
const btnSecondary: React.CSSProperties = { padding: "5px 10px", background: "white", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 };
